/**
 * The URLs of this session's history entries, by React Router's history
 * index (`window.history.state.idx`). Lets a back control step back through
 * the real history when the entry behind the current one is the page it
 * means to return to, instead of pushing that page again.
 *
 * Only entries visited in this page load are known: after a reload the map
 * starts empty and back falls back to pushing the referrer.
 */
const entries = new Map<number, string>()

/**
 * A paged list's cursor params: page position and page size. Left out
 * wherever a list is matched by what it shows rather than how far it was
 * scrolled (back controls, list cache keys).
 */
const CURSOR_PARAMS = ['page', 'perPage', 'skip']

/** The cursor params are left out so a paged list matches its own entry. */
export function normalizeEntryUrl(pathname: string, search: string): string {
    const params = new URLSearchParams(search)
    for (const key of CURSOR_PARAMS) params.delete(key)
    const query = params.toString()
    return pathname + (query ? '?' + query : '')
}

function currentIndex(): number | undefined {
    const idx = (window.history.state as { idx?: unknown } | null)?.idx
    return typeof idx === 'number' ? idx : undefined
}

export function recordHistoryEntry(url: string, isPush: boolean): void {
    const idx = currentIndex()
    if (idx === undefined) return
    entries.set(idx, url)
    if (!isPush) return
    // A push after going back forks history: the entries ahead are gone.
    for (const key of entries.keys()) {
        if (key > idx) entries.delete(key)
    }
}

/** The URL of the entry one step back, when this session visited it. */
export function previousHistoryEntry(): string | undefined {
    const idx = currentIndex()
    if (idx === undefined || idx < 1) return undefined
    return entries.get(idx - 1)
}
